import React, {Suspense, lazy, useEffect, useMemo, useState} from 'react';

import {Route, Routes} from 'react-router-dom';

import {LoaderSpinner} from '../components/LoaderSpinner';
import {NotFound} from '../components/NotFound';
import {getUserRoles} from '../components/ProtectedComponent';
import {Home} from '../pages/home';

const Organizations = lazy(() => import('../pages/organizations'));
const Roles = lazy(() => import('../pages/roles'));
const Plans = lazy(() => import('../pages/plans'));
const Users = lazy(() => import('../pages/users'));
const Locations = lazy(() => import('../pages/locations'));
const LocationsTypes = lazy(() => import('../pages/locationsTypes'));
const Projects = lazy(() => import('../pages/projects'));
const Bills = lazy(() => import('../pages/bills'));
const NewForm = lazy(() => import('../pages/forms/form/NewForm'));
const Forms = lazy(() => import('../pages/forms'));
const Documents = lazy(() => import('../pages/documents'));
const EventType = lazy(() => import('../pages/events'));
const SupportEvents = lazy(() => import('../pages/supportEvents'));
const SupportJourneys = lazy(() => import('../pages/supportJourneys'));
const Analytics = lazy(() => import('../pages/analytics'));
const Report = lazy(() => import('../pages/analytics/Report'));
const Objectives = lazy(() => import('../pages/objectives'));
const Tours = lazy(() => import('../pages/tours'));
const Schedule = lazy(() => import('../pages/schedule'));
const Incidents = lazy(() => import('../pages/incidents'));
const CustomFields = lazy(() => import('../pages/customFields'));
const Kpis = lazy(() => import('../pages/kpis'));
const AssignKpis = lazy(() => import('../pages/assignKpis'));
const Periods = lazy(() => import('../pages/periods'));
const Policies = lazy(() => import('../pages/policies'));
const SupportUsers = lazy(() => import('../pages/supportUsers'));
const BillsTypes = lazy(() => import('../pages/billsTypes'));
const SubPolicies = lazy(() => import('../pages/policies/SubPolicies'));
const ContentReportChecks = lazy(
  () => import('../pages/reports/ContentReportChecks'),
);
const ContentReportMileage = lazy(
  () => import('../pages/reports/ContentReportMileage'),
);

interface IRouteMapping {
  allowedRoles: string[];
  path: string;
  element: React.JSX.Element;
}

const routeMapping: IRouteMapping[] = [
  {
    path: '/organizations',
    allowedRoles: ['super-admin'],
    element: <Organizations />,
  },
  {
    path: '/users',
    allowedRoles: ['super-admin', 'project-admin'],
    element: <Users />,
  },
  {
    path: '/users/:id',
    allowedRoles: ['super-admin', 'project-admin'],
    element: <Users />,
  },
  {
    path: '/roles',
    allowedRoles: ['super-admin'],
    element: <Roles />,
  },
  {
    path: '/locations',
    allowedRoles: ['super-admin', 'happify-location-service.location.admin'],
    element: <Locations />,
  },
  {
    path: '/locationsTypes',
    allowedRoles: ['super-admin'],
    element: <LocationsTypes />,
  },
  {
    path: '/event-type',
    allowedRoles: ['super-admin'],
    element: <EventType />,
  },
  {
    path: '/plans',
    allowedRoles: ['super-admin'],
    element: <Plans />,
  },
  {
    path: '/projects',
    allowedRoles: ['super-admin'],
    element: <Projects />,
  },
  {
    path: '/forms/edit/',
    allowedRoles: ['super-admin', 'happify-forms-service.forms.create'],
    element: <NewForm />,
  },
  {
    path: '/forms/edit/:id',
    allowedRoles: ['super-admin', 'happify-forms-service.forms.edit'],
    element: <NewForm />,
  },
  {
    path: '/forms',
    allowedRoles: ['super-admin', 'happify-forms-service.forms.list'],
    element: <Forms />,
  },
  {
    path: '/objectives',
    allowedRoles: ['super-admin'],
    element: <Objectives />,
  },
  {
    path: '/incidents',
    allowedRoles: ['super-admin'],
    element: <Incidents />,
  },
  {
    path: '/support-events',
    allowedRoles: ['super-admin'],
    element: <SupportEvents />,
  },
  {
    path: '/support-journeys',
    allowedRoles: ['super-admin'],
    element: <SupportJourneys />,
  },
  {
    path: '/bills',
    allowedRoles: ['super-admin', 'org-admin'],
    element: <Bills />,
  },
  {
    path: '/kpis',
    allowedRoles: [
      'super-admin',
      'happify-kpi-service.kpi.template-load',
      'happify-kpi-service.kpi.admin',
    ],
    element: <Kpis />,
  },
  {
    path: '/assign-kpis',
    allowedRoles: [
      'super-admin',
      'happify-kpi-service.kpi.template-load',
      'happify-kpi-service.kpi.admin',
    ],
    element: <AssignKpis />,
  },
  {
    path: '/custom-fields',
    allowedRoles: ['super-admin'],
    element: <CustomFields />,
  },
  {
    path: '/periods',
    allowedRoles: ['super-admin', 'happify-kpi-service.kpi-period.view'],
    element: <Periods />,
  },
  {
    path: '/support-users',
    allowedRoles: ['super-admin'],
    element: <SupportUsers />,
  },
  {
    path: '/schedule',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Schedule />,
  },
  {
    path: '/tours',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Tours />,
  },
  {
    path: '/analytics',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Analytics />,
  },
  {
    path: '/analytics/report',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Report />,
  },
  {
    path: '/analytics/report/:id',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Report />,
  },
  {
    path: '/documents',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <Documents />,
  },
  {
    path: '/billsTypes',
    allowedRoles: ['super-admin'],
    element: <BillsTypes />,
  },
  {
    path: '/policies',
    allowedRoles: ['super-admin'],
    element: <Policies />,
  },
  {
    path: '/policies/sub-policies',
    allowedRoles: ['super-admin'],
    element: <SubPolicies />,
  },
  {
    path: '/policies/sub-policies/:idOrganization/:id',
    allowedRoles: ['super-admin'],
    element: <SubPolicies />,
  },
  {
    path: '/report-checks',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <ContentReportChecks />,
  },
  {
    path: '/report-mileage',
    allowedRoles: ['super-admin', 'supervisor'],
    element: <ContentReportMileage />,
  },
];

export function AppRouter() {
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    setRoles(getUserRoles());
  }, []);

  const routes = useMemo(() => {
    return routeMapping
      .filter(route => {
        return route.allowedRoles.some(role => roles.includes(role));
      })
      .map(route => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <Suspense fallback={<LoaderSpinner />}>{route.element}</Suspense>
          }
        />
      ));
  }, [roles]);

  if (roles.length === 0) {
    return <LoaderSpinner />;
  }

  return (
    <Routes>
      {routes}
      <Route path='/' element={<Home />} />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}
